import { useContext, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import AppContext from './context/AppContext';
import Game from './components/Game/Game';
import Results from './components/Game/Results/Results';
import Button from './components/UI/Button/Button';
import Step from './components/UI/Step/Step';
import Header from './components/UI/Header/Header';
import Livery from './components/UI/Livery/Livery';
import Opponents from './components/UI/Opponents/Opponents';
import Details from './components/UI/Details/Details';
import Alert from './components/UI/Alert/Alert';
import useMediaQueries from './hooks/useMediaQueries';

import styles from './App.module.scss';

import crewLogo from './assets/crew-logo.svg';
import crewMark from './assets/crew-mark.svg';
import williamsPartnerLogo from './assets/WR_Supplier_White.svg';

import car1 from './assets/car-1.svg';
import car2 from './assets/car-2.svg';
import car3 from './assets/car-3.svg';
import carCpu1 from './assets/car-cpu-1.svg';
import carCpu2 from './assets/car-cpu-2.svg';

/* global APP_CONFIG */

const cars = [
  { src: car1, color: '#E37F23' }, 
  { src: car2, color: '#D2232A' }, 
  { src: car3, color: '#838383' }
];

const opponents = [
  {
    ...APP_CONFIG.opponents.driver1,
    sprite: {
      src: carCpu1,
      color: '#0000ff'
    }
  },
  {
    ...APP_CONFIG.opponents.driver2,
    sprite: {
      src: carCpu2,
      color: '#0000ff'
    }
  }
];

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.25
    }
  }
};

const stepAnim = {
  hidden: { opacity: 0, y: 30 },
  show: { 
    opacity: 1,
    y: 0,
    transition: {
      type: "tween",
      duration: 0.5, 
      ease: "circOut"
    }
  }
};

function Step1({ onChange, ...other }) {
  return (
    <Step heading={`Select your livery`} number={1} {...other}>
      <Livery cars={cars} onChange={onChange}/>
    </Step>
  )
};

function Step2({ onChange, ...other }) {
  return (
    <Step heading={`Choose your opponent`} number={2} {...other}>
      <Opponents opponents={opponents} onChange={onChange}/>
    </Step>
  )
};

function Step3({ onSubmit, ...other }){
  return (
    <Step heading={`Enter your details`} number={3} {...other}>
      <Details onSubmit={onSubmit}/>
    </Step>
  )
}

function App() {
  const { isLgMin } = useMediaQueries();
  const [livery, setLivery] = useState(cars[0]);
  const [opponent, setOpponent] = useState(opponents[0]);
  const [step, setStep] = useState(0);
  const [message, setMessage] = useState(null);
  const [windowHeight, setWindowHeight] = useState( window.innerHeight );
  const { state, dispatch } = useContext(AppContext);

  function handleSubmit(formData, response){
    if( !response.isPlayLimitReached ) {
      dispatch({ type: 'SET_PLAYER', payload: {
        ...response,
        formData,
        name: formData.name,
        team: 'Crew Clothing'
      }});      
      dispatch({ type: 'PLAYING' });
    } else {
      setMessage( response.message );
    }
  }

  useEffect(() => {
    dispatch({ type: 'SET_LIVERY', payload: livery });
  }, [dispatch, livery]);

  
  useEffect(() => {
    dispatch({ type: 'SET_CPU', payload: opponent });
  }, [dispatch, opponent]);

  useEffect(() => {
    function handleResize(){
      setWindowHeight( window.innerHeight );
    }
    
    window.addEventListener('resize', handleResize );

    return () => {
      window.removeEventListener('resize', handleResize );
    }
  },[])

  return (
    <>
      {!state.isPlaying ? (
        <div className={styles.container} style={{ minHeight: windowHeight }}>
          <div className={styles.toolbar}>
            <a className={styles.siteLink} href={APP_CONFIG.links.website.href}>
                {APP_CONFIG.links.website.label}
            </a>
          </div>

          <motion.div variants={container} initial="hidden" animate="show" className={styles.inner}>
            <a className={styles.logo} href={APP_CONFIG.links.website.href}>
              <img className={styles.logoImg} src={crewLogo} alt="Crew Clothing Company" />
            </a>

            <div className={styles.body}>

                {step === 0 && (
                  <motion.div className={styles.step} variants={stepAnim}>
                    <Header/>
                    <p className={styles.intro}>{APP_CONFIG.intro}</p>
                    <Button onClick={e => setStep(1)} isLarge={true}>Start</Button>
                  </motion.div>
                )}

                {step === 1 && (
                  <motion.div className={styles.step} variants={stepAnim}>
                    <Step1 onChange={data => setLivery(data)}/>
                    <Button onClick={e => setStep(2)} isLarge={true}>Next</Button>
                  </motion.div>
                )}

                {step === 2 && (
                  <motion.div className={styles.step} variants={stepAnim}>
                    <Step2 onChange={data => setOpponent(data)}/>
                    <Button onClick={e => setStep(3)} isLarge={true}>Next</Button>
                  </motion.div>
                )}

                {step === 3 && (
                  <motion.div className={styles.step} variants={stepAnim}>
                    <Step3 onSubmit={handleSubmit}/>
                    <Alert message={message}/>
                  </motion.div>
                )}
                
              </div>
          </motion.div>

          <a className={styles.termsLink} href={APP_CONFIG.links.terms.href} target="_blank" rel="noreferrer">
            {APP_CONFIG.links.terms.label}
          </a>
        </div>
      ) : (
        <>
          <img className={styles.logoCrew} src={isLgMin ? crewLogo : crewMark} alt="Crew Clothing Company" />
          <img className={styles.logoWilliams} src={williamsPartnerLogo} alt="Williams Racing Offical Partner" />
        </>
      )}

      {state.isRaceComplete &&
         <Results/>
      }
     
      <Game/> 
    </>
  );
}

export default App;
